"use client";

import * as React from "react";
import Link from "next-shims/Link";
import { usePathname } from "next-shims/navigation";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Container,
  Button,
  Drawer,
  Stack,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { ColorModeContext } from "../../app/providers";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../../i18n/I18nProvider";
type NavItem = { key: string; href: (locale: string) => string };

const NAV: NavItem[] = [
  { key: "nav_home", href: (l) => `/${l}` },
  { key: "nav_properties", href: (l) => `/${l}/properties` },
  { key: "nav_contact", href: (l) => `/${l}/contact` },
  { key: "nav_reviews", href: (l) => `/${l}/reviews` },
];

const LEGAL: NavItem[] = [
  { key: "nav_privacy", href: (l) => `/${l}/privacy-policy` },
  { key: "nav_terms", href: (l) => `/${l}/terms` },
];

export default function Header({ locale }: { locale: string }) {
  const { mode, toggle } = React.useContext(ColorModeContext);
  const { t } = useI18n();

  const logoSrc =
    mode === "dark" ? "/brand/rp-white-96.png" : "/brand/rp-dark-96.png";

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const pathname = usePathname();

  const [open, setOpen] = React.useState(false);

  const isActive = (href: string) => {
    if (!pathname) return false;
    return pathname === href || pathname.startsWith(href + "/");
  };

  const NavButtons = (
    <>
      {NAV.map((it) => {
        const href = it.href(locale);
        const active = isActive(href);
        return (
          <Button
            key={it.key}
            component={Link}
            href={href}
            variant="text"
            sx={{
              borderRadius: 999,
              fontWeight: 800,
              ...(active ? { bgcolor: "action.selected" } : {}),
            }}
          >
            {t(it.key)}
          </Button>
        );
      })}
    </>
  );

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ py: 1, gap: 1 }}>
          {/* Brand */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Link
              href={`/${locale}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                textDecoration: "none",
              }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt={t("brand_name")}
                sx={{ width: 72, height: 72, flexShrink: 0 }}
              />
            </Link>
          </Box>

          {/* Desktop nav */}
          {isMdUp ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {NavButtons}

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Button
                component={Link}
                href={`/${locale}/properties?label=${encodeURIComponent(
                  "Hot Offer"
                )}`}
                variant="contained"
                sx={{ borderRadius: 999, fontWeight: 900 }}
              >
                {t("nav_hot_offers")}
              </Button>

              <LanguageSwitcher />

              <IconButton
                onClick={toggle}
                aria-label="Toggle theme"
                sx={{ ml: 0.5 }}
              >
                {mode === "dark" ? (
                  <LightModeOutlinedIcon />
                ) : (
                  <DarkModeOutlinedIcon />
                )}
              </IconButton>
            </Box>
          ) : (
            // Mobile actions
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LanguageSwitcher />
              <IconButton onClick={toggle} aria-label="Toggle theme">
                {mode === "dark" ? (
                  <LightModeOutlinedIcon />
                ) : (
                  <DarkModeOutlinedIcon />
                )}
              </IconButton>
              <IconButton onClick={() => setOpen(true)} aria-label="Open menu">
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile drawer */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 320, p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography sx={{ fontWeight: 900 }}>{t("menu_title")}</Typography>
            <IconButton onClick={() => setOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <List disablePadding>
            {NAV.map((it) => {
              const href = it.href(locale);
              const active = isActive(href);
              return (
                <ListItemButton
                  key={it.key}
                  component={Link}
                  href={href}
                  onClick={() => setOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    ...(active ? { bgcolor: "action.selected" } : {}),
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{ fontWeight: 800 }}
                    primary={t(it.key)}
                  />
                </ListItemButton>
              );
            })}
          </List>

          <Box sx={{ mt: 1.5 }}>
            <Button
              fullWidth
              component={Link}
              href={`/${locale}/properties?label=${encodeURIComponent(
                "Hot Offer"
              )}`}
              variant="contained"
              sx={{ borderRadius: 3, fontWeight: 900 }}
              onClick={() => setOpen(false)}
            >
              {t("nav_hot_offers")}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("legal")}
          </Typography>

          <List disablePadding>
            {LEGAL.map((it) => {
              const href = it.href(locale);
              const active = isActive(href);
              return (
                <ListItemButton
                  key={it.key}
                  component={Link}
                  href={href}
                  onClick={() => setOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    ...(active ? { bgcolor: "action.selected" } : {}),
                  }}
                >
                  <ListItemText
                    primaryTypographyProps={{ fontWeight: 700 }}
                    primary={t(it.key)}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
